import {AttendeeDetailsProps, BookingQuestionAnswer} from "./attendee-details.types";

/**
 * AttendeeDetails entity - asymmetric entity for the per-attendee details modal.
 *
 * Contains the basic attendee fields (same as AttendeeAnalytics) plus optional
 * rich fields that are populated only by v1. The modal renders booking-questions
 * and purchase-history sections conditionally using hasBookingQuestions() and
 * hasPurchaseHistory().
 *
 * Pure TypeScript - no React imports.
 */
export class AttendeeDetails {
  private readonly props: AttendeeDetailsProps;

  private constructor(props: AttendeeDetailsProps) {
    this.props = props;
  }

  /**
   * Creates an AttendeeDetails entity with validation.
   * @throws Error if emailAddress or reference is empty
   * @throws Error if amount is negative
   */
  static create(props: AttendeeDetailsProps): AttendeeDetails {
    const emailAddress = (props.emailAddress ?? "").trim();
    const reference = (props.reference ?? "").trim();
    const amount = props.amount ?? 0;

    if (!emailAddress) {
      throw new Error("emailAddress cannot be empty");
    }
    if (!reference) {
      throw new Error("reference cannot be empty");
    }
    if (amount < 0) {
      throw new Error("amount cannot be negative");
    }

    return new AttendeeDetails({
      firstName: props.firstName ?? "",
      lastName: props.lastName ?? "",
      emailAddress,
      amount,
      currency: props.currency ?? "",
      reference,
      createdAt: props.createdAt,
      bookingQuestions: props.bookingQuestions ?? [],
      totalPurchases: props.totalPurchases,
      totalVolume: props.totalVolume,
      phoneNumber: props.phoneNumber ?? "",
      callPrice: props.callPrice,
      callPriceCurrency: props.callPriceCurrency ?? "",
    });
  }

  get firstName(): string {
    return this.props.firstName;
  }

  get lastName(): string {
    return this.props.lastName;
  }

  get emailAddress(): string {
    return this.props.emailAddress;
  }

  get amount(): number {
    return this.props.amount;
  }

  get currency(): string {
    return this.props.currency;
  }

  get reference(): string {
    return this.props.reference;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get bookingQuestions(): BookingQuestionAnswer[] {
    return this.props.bookingQuestions ?? [];
  }

  get totalPurchases(): number | undefined {
    return this.props.totalPurchases;
  }

  get totalVolume(): number | undefined {
    return this.props.totalVolume;
  }

  get phoneNumber(): string {
    return this.props.phoneNumber ?? "";
  }

  get callPrice(): number | undefined {
    return this.props.callPrice;
  }

  get callPriceCurrency(): string {
    return this.props.callPriceCurrency ?? "";
  }

  /**
   * Returns the full name (firstName + lastName), trimmed.
   */
  get fullName(): string {
    return `${this.props.firstName} ${this.props.lastName}`.trim();
  }

  /**
   * Returns true if the attendee has paid.
   * Uses callPrice when available (v1), otherwise falls back to amount (v2 inline).
   */
  isPaid(): boolean {
    if (this.props.callPrice !== undefined) {
      return this.props.callPrice > 0;
    }
    return this.props.amount > 0;
  }

  /**
   * Returns the display price.
   * Uses callPrice/callPriceCurrency when available (v1), otherwise amount/currency (v2 inline).
   */
  getDisplayPrice(): {amount: number; currency: string} {
    if (this.props.callPrice !== undefined) {
      return {
        amount: this.props.callPrice,
        currency: this.props.callPriceCurrency ?? "",
      };
    }
    return {
      amount: this.props.amount,
      currency: this.props.currency,
    };
  }

  /**
   * Returns true if booking questions with answers exist (v1 only).
   */
  hasBookingQuestions(): boolean {
    const questions = this.props.bookingQuestions ?? [];
    return questions.length > 0;
  }

  /**
   * Returns true if purchase history data exists (v1 only - totalPurchases/totalVolume).
   */
  hasPurchaseHistory(): boolean {
    return (
      this.props.totalPurchases !== undefined && this.props.totalVolume !== undefined
    );
  }

  /**
   * Returns true if phone number exists.
   */
  hasPhoneNumber(): boolean {
    return !!this.props.phoneNumber;
  }
}
